// src/modules/callSync/pages/CallTargetsAdmin.jsx
// Daily call targets (STEP 15). Resolution is USER → BRANCH → GLOBAL.
//
// A target row carries two things: the NUMBERS a rep is asked to hit, and the
// RULE for what fulfils them. Both are inherited together — a rep on a branch
// target is judged by that branch's rule, never by the global one.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Typography, Space, Button, Form, Select, InputNumber, message, Tag,
  Alert, Modal, Switch, Divider, Tooltip,
} from 'antd';
import {
  AimOutlined, ReloadOutlined, PlusOutlined, EditOutlined, InfoCircleOutlined,
} from '@ant-design/icons';
import { fetchCallTargets, saveCallTarget } from '../api';
import { CREDIT_MODE_OPTIONS, creditModeMeta } from '../constants';
import { fetchAssignableCsrs } from '../../leads/api';
import API from '../../../services/api';

const { Title, Text, Paragraph } = Typography;

const SCOPE_COLOR = { GLOBAL: 'blue', BRANCH: 'geekblue', USER: 'purple' };

/** The rule as one plain-English sentence, so nobody has to infer it. */
function ruleSentence(creditMode, sameDay) {
  const base = {
    DIALLED_ONLY: 'A call counts as soon as the phone places it, saved result or not.',
    DIALLED_AND_SAVED: 'A call counts once its result is saved in the CRM — connected or not.',
    CONNECTED_AND_SAVED: 'A call counts only if the customer answered AND the result is saved.',
  }[creditMode] || '';
  const deadline = creditMode === 'DIALLED_ONLY'
    ? ''
    : sameDay
      ? ' The result must be saved the same business day, or the credit is lost.'
      : ' A result saved later still counts, backdated to the day of the call.';
  return `${base}${deadline} Cancelled, failed and expired calls never count.`;
}

export default function CallTargetsAdmin() {
  const [data, setData] = useState({ targets: [], defaultDailyTarget: 100 });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [csrs, setCsrs] = useState([]);
  const [branches, setBranches] = useState([]);
  const [form] = Form.useForm();
  const scope = Form.useWatch('scope', form);
  const creditMode = Form.useWatch('creditMode', form);
  const sameDay = Form.useWatch('requireSameBusinessDaySave', form);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchCallTargets();
      setData(res.data.data || { targets: [] });
    } catch (err) {
      message.error(err?.response?.data?.message || 'Could not load targets');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    fetchAssignableCsrs().then((r) => setCsrs(r?.data?.data || [])).catch(() => {});
    API.get('/branches').then((r) => setBranches(r?.data?.data || r?.data || [])).catch(() => {});
  }, []);

  const openNew = () => {
    setEditing(null);
    form.resetFields();
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    form.setFieldsValue({
      scope: row.scope,
      scopeId: row.scopeId?._id || row.scopeId || null,
      dailyCallTarget: row.dailyCallTarget,
      dailyConnectedTarget: row.dailyConnectedTarget || 0,
      dailyTalkTimeMinutesTarget: row.dailyTalkTimeMinutesTarget || 0,
      creditMode: row.fulfilment?.creditMode || 'DIALLED_ONLY',
      requireSameBusinessDaySave: row.fulfilment?.requireSameBusinessDaySave !== false,
    });
    setOpen(true);
  };

  const submit = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      await saveCallTarget({
        scope: v.scope,
        scopeId: v.scope === 'GLOBAL' ? null : v.scopeId,
        dailyCallTarget: v.dailyCallTarget,
        dailyConnectedTarget: v.dailyConnectedTarget || 0,
        dailyTalkTimeMinutesTarget: v.dailyTalkTimeMinutesTarget || 0,
        creditMode: v.creditMode,
        requireSameBusinessDaySave: v.requireSameBusinessDaySave,
      });
      message.success('Target saved');
      setOpen(false);
      setEditing(null);
      form.resetFields();
      load();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.message || 'Could not save the target');
    } finally { setSaving(false); }
  };

  // 0 means "not tracked" — say so rather than showing a target of zero.
  const optional = (v, suffix = '') => (v ? `${v}${suffix}` : <Text type="secondary">—</Text>);

  const columns = [
    {
      title: 'Scope',
      dataIndex: 'scope',
      render: (v) => <Tag color={SCOPE_COLOR[v] || 'default'}>{v}</Tag>,
    },
    {
      title: 'Applies to',
      render: (_, r) => (r.scope === 'GLOBAL'
        ? 'Everyone'
        : r.scopeId?.name || r.scopeId?.code || String(r.scopeId || '—')),
    },
    { title: 'Calls / day', dataIndex: 'dailyCallTarget' },
    {
      title: 'Connected / day',
      dataIndex: 'dailyConnectedTarget',
      render: (v) => optional(v),
    },
    {
      title: 'Talk time / day',
      dataIndex: 'dailyTalkTimeMinutesTarget',
      render: (v) => optional(v, 'm'),
    },
    {
      title: 'What counts',
      render: (_, r) => {
        const mode = r.fulfilment?.creditMode || 'DIALLED_ONLY';
        const meta = creditModeMeta(mode);
        return (
          <Tooltip title={ruleSentence(mode, r.fulfilment?.requireSameBusinessDaySave !== false)}>
            <Tag color={mode === 'DIALLED_ONLY' ? 'default' : 'green'}>{meta.short}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      render: (v) => <Tag color={v ? 'green' : 'default'}>{v ? 'Yes' : 'No'}</Tag>,
    },
    {
      title: '',
      width: 60,
      render: (_, r) => (
        <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(r)} />
      ),
    },
  ];

  const defaultMode = data.defaultRule?.creditMode || 'DIALLED_ONLY';

  return (
    <div style={{ padding: '8px 0' }}>
      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
        <Title level={4} style={{ margin: 0 }}><AimOutlined /> Daily Calling Targets</Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openNew}>Set Target</Button>
        </Space>
      </Space>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="How a target is chosen"
        description={(
          <>
            <Paragraph style={{ marginBottom: 6 }}>
              The most specific row wins: a <Text code>USER</Text> target overrides
              a <Text code>BRANCH</Text> target, which overrides <Text code>GLOBAL</Text>.
              With none set, everyone gets the system default
              of <Text strong>{data.defaultDailyTarget}</Text> calls a day
              on <Text strong>{creditModeMeta(defaultMode).label}</Text>.
            </Paragraph>
            <Paragraph style={{ marginBottom: 0 }}>
              The <Text strong>rule travels with the row</Text> — a rep on a branch target
              is judged by that branch&apos;s rule, not the global one. A call&apos;s verdict is
              frozen when it is earned, so changing a rule never rewrites past days.
            </Paragraph>
          </>
        )}
      />

      <Card size="small">
        <Table
          rowKey="_id"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={data.targets}
          pagination={false}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: `No targets configured — the default of ${data.defaultDailyTarget} calls/day applies` }}
        />
      </Card>

      <Modal
        title={editing ? 'Edit calling target' : 'Set a calling target'}
        open={open}
        onCancel={() => { setOpen(false); setEditing(null); }}
        onOk={submit}
        okButtonProps={{ loading: saving }}
        width={560}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            scope: 'GLOBAL',
            dailyCallTarget: 100,
            dailyConnectedTarget: 0,
            dailyTalkTimeMinutesTarget: 0,
            creditMode: 'DIALLED_AND_SAVED',
            requireSameBusinessDaySave: true,
          }}
        >
          <Form.Item name="scope" label="Scope" rules={[{ required: true }]}>
            <Select disabled={!!editing} options={[
              { value: 'GLOBAL', label: 'Everyone (global default)' },
              { value: 'BRANCH', label: 'A branch' },
              { value: 'USER', label: 'A single sales rep' },
            ]} />
          </Form.Item>

          {scope === 'BRANCH' && (
            <Form.Item name="scopeId" label="Branch" rules={[{ required: true }]}>
              <Select
                showSearch
                disabled={!!editing}
                optionFilterProp="label"
                options={branches.map((b) => ({ value: b._id, label: b.name || b.code }))}
              />
            </Form.Item>
          )}
          {scope === 'USER' && (
            <Form.Item name="scopeId" label="Sales rep" rules={[{ required: true }]}>
              <Select
                showSearch
                disabled={!!editing}
                optionFilterProp="label"
                options={csrs.map((c) => ({ value: c._id, label: `${c.name}${c.employeeCode ? ` (${c.employeeCode})` : ''}` }))}
              />
            </Form.Item>
          )}

          <Divider orientation="left" plain style={{ marginTop: 4 }}>Daily numbers</Divider>

          <Form.Item name="dailyCallTarget" label="Calls per day" rules={[{ required: true }]}>
            <InputNumber min={0} max={1000} style={{ width: '100%' }} />
          </Form.Item>

          <Space size={12} style={{ display: 'flex' }}>
            <Form.Item
              name="dailyConnectedTarget"
              label="Connected calls per day"
              tooltip="Calls the customer actually answered. 0 = not tracked."
              style={{ flex: 1 }}
            >
              <InputNumber min={0} max={1000} style={{ width: '100%' }} placeholder="0 = off" />
            </Form.Item>
            <Form.Item
              name="dailyTalkTimeMinutesTarget"
              label="Talk time per day (minutes)"
              tooltip="Counts only measured talk time, never an estimate. 0 = not tracked."
              style={{ flex: 1 }}
            >
              <InputNumber min={0} max={1440} style={{ width: '100%' }} placeholder="0 = off" />
            </Form.Item>
          </Space>

          <Divider orientation="left" plain>What fulfils the target</Divider>

          <Form.Item name="creditMode" label="Credit a call when it was…" rules={[{ required: true }]}>
            <Select
              options={CREDIT_MODE_OPTIONS.map((o) => ({
                value: o.value,
                label: o.label,
              }))}
              optionRender={(o) => {
                const meta = creditModeMeta(o.value);
                return (
                  <div style={{ padding: '2px 0' }}>
                    <div>{meta.label}</div>
                    <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'normal' }}>
                      {meta.help}
                    </Text>
                  </div>
                );
              }}
            />
          </Form.Item>

          {creditMode !== 'DIALLED_ONLY' && (
            <Form.Item
              name="requireSameBusinessDaySave"
              label="Result must be saved the same business day"
              valuePropName="checked"
              tooltip="Off means a result saved days later still counts, backdated to the day of the call."
            >
              <Switch />
            </Form.Item>
          )}

          <Alert
            type={creditMode === 'DIALLED_ONLY' ? 'warning' : 'success'}
            showIcon
            icon={<InfoCircleOutlined />}
            message="The rule this sets"
            description={ruleSentence(creditMode, sameDay)}
          />
        </Form>
      </Modal>
    </div>
  );
}
